import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import storeService from '../services/storeService';

const StoreContext = createContext();

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  const { user } = useSelector((state) => state.auth);
  const [currentStore, setCurrentStore] = useState(null);
  const [availableStores, setAvailableStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserStores();
    }
  }, [user]);

    const fetchUserStores = async () => {
        try {
            setLoading(true);
            console.log('user:', user);
            console.log('user.storeId:', user?.storeId);
            console.log('user.globalRole:', user?.globalRole);

            let stores = [];

            if (user.globalRole === 'SUPER_ADMIN') {
                stores = await storeService.getAllStores();
            } else {
                const storeId = user.storeId
                    || user.store?.id
                    || user.role?.store?.id;

                console.log('resolved storeId:', storeId);

                if (storeId) {
                    try {
                        const userStore = await storeService.getStoreById(storeId);
                        console.log('userStore fetched:', userStore); // ← add this
                        stores = [userStore];
                    } catch (err) {
                        console.error('Failed to fetch store:', err.response?.data || err.message);
                        localStorage.removeItem('currentStoreId');
                    }
                }
            }
            console.log('stores fetched:', stores);
            setAvailableStores(stores);

            if (stores.length === 0) {
                setLoading(false);
                return;
            }

            const savedStoreId = localStorage.getItem('currentStoreId');

            // 1. Try last used store
            if (savedStoreId) {
                const lastStore = stores.find(s => s.id?.toString() === savedStoreId);
                if (lastStore) {
                    // Last store still valid → use it
                    setCurrentStore(lastStore);
                    setLoading(false);
                    return;
                } else {
                    // Last store no longer exists → clear stale value
                    localStorage.removeItem('currentStoreId');
                }
            }

            // 2. Only one store → auto select
            if (stores.length === 1) {
                setCurrentStore(stores[0]);
                localStorage.setItem('currentStoreId', stores[0].id);
                setLoading(false);
                return;
            }

            // 3. STORE_USER → auto select their assigned store
            if (user.globalRole !== 'SUPER_ADMIN' && user.storeId) {
                const userStore = stores.find(s => s.id === user.storeId);
                if (userStore) {
                    setCurrentStore(userStore);
                    localStorage.setItem('currentStoreId', userStore.id);
                    setLoading(false);
                    return;
                }
            }

            // 4. SUPER_ADMIN with multiple stores → default to first
            setCurrentStore(stores[0]);
            localStorage.setItem('currentStoreId', stores[0].id);

        } catch (error) {
            console.error('Failed to fetch user stores:', error);
            // Clear stale store on error
            localStorage.removeItem('currentStoreId');
        } finally {
            setLoading(false);
        }
    };

  const switchStore = (store) => {
    setCurrentStore(store);
    localStorage.setItem('currentStoreId', store.id);
    
    // Reload page to refresh all data for new store context
    window.location.reload();
  };

  const hasAccessToStore = (storeId) => {
    if (user?.globalRole === 'SUPER_ADMIN') return true;
    return user?.storeId === storeId;
  };

  const requireStoreSelection = () => {
    // For SUPER_ADMIN working with store-specific data
    if (user?.globalRole === 'SUPER_ADMIN' && !currentStore) {
      return true;
    }
    return false;
  };

  const value = {
    currentStore,
    availableStores,
    loading,
    switchStore,
    hasAccessToStore,
    refreshStores: fetchUserStores,
    isSuperAdmin: user?.globalRole === 'SUPER_ADMIN',
    isStoreUser: user?.globalRole === 'STORE_USER',
    requireStoreSelection: requireStoreSelection()
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};